import type { ChatMessageContent } from "schemas/generated/chatMessage";

import React, { useState } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Collapsible } from "@replo/design-system/components/collapsible/Collapsible";
import { Popover } from "@replo/design-system/components/popover/Popover";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import CanceledCard from "@v2/features/chat/components/chat-elements/chat-message/CanceledCard";
import { MarkdownRenderer } from "@v2/features/chat/components/chat-elements/chat-message/MarkdownRenderer";
import { useChatId } from "@v2/features/chat/hooks/useChatId";
import { useViewingVersion } from "@v2/features/chat/hooks/useViewingVersion";
import { useCurrentChatContext } from "@v2/features/chat/providers/CurrentChatContext";
import { useDefaultSelectedElementDesignation } from "@v2/features/modifiers/store/ModifiersStoreContext";
import {
  VersionCard,
  VersionCardSkeleton,
} from "@v2/features/version-history/VersionCard";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { chatRouter, chatUtils } from "@v2/trpc/routes/chat";
import { CircleX, ImageIcon, Lightbulb, MoreHorizontal } from "lucide-react";
import { parseJsonOrNull } from "replo-utils/lib/json";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";
import {
  chatBuildErrorItemSchema,
  chatGeneralErrorItemSchema,
  chatProductItemSchema,
  chatRevertItemSchema,
} from "schemas/chatMessage";

type TextMessageContent = Extract<ChatMessageContent[number], { type: "text" }>;

const extractThinkBlocks = (text: string) => {
  const thinkRegex = /<think>([\S\s]*?)<\/think>/g;
  const thinkBlocks: string[] = [];
  let match;

  // Check for streaming think block (opening tag without closing tag)
  const streamingThinkMatch = text.match(/^<think>([\S\s]*?)(?!<\/think>)$/);
  const isStreamingThink = streamingThinkMatch && !text.includes("</think>");

  if (isStreamingThink) {
    const streamingContent = streamingThinkMatch[1] ?? "";
    return {
      thinkBlocks: [],
      cleanedText: "",
      hasThinkBlocks: false,
      isStreamingThink: true,
      streamingThinkContent: streamingContent,
    };
  }

  // Extract completed think blocks
  match = thinkRegex.exec(text);
  while (match !== null) {
    thinkBlocks.push(match[1]?.trim() ?? "");
    match = thinkRegex.exec(text);
  }

  // Remove think blocks from the original text
  const cleanedText = text.replace(thinkRegex, "").trim();

  return {
    thinkBlocks,
    cleanedText,
    hasThinkBlocks: thinkBlocks.length > 0,
    isStreamingThink: false,
    streamingThinkContent: "",
  };
};

const ProductRenderer = ({ text }: { text: string }) => {
  const product = chatProductItemSchema.parse(JSON.parse(text));

  let featuredMediaUrl = product.featuredMedia?.url ?? "";
  if (product.media?.[0]?.type === "image") {
    featuredMediaUrl = product.media[0].url;
  }

  return (
    <div className="flex gap-3 border border-border rounded-lg p-1 w-[200px]">
      <div className="flex-shrink-0">
        {featuredMediaUrl ? (
          <div className="bg-surface h-8 w-8 min-h-8 min-w-8 flex-shrink-0 flex items-center justify-center rounded-md overflow-hidden">
            <img
              src={featuredMediaUrl}
              alt={product.title}
              className="h-full w-full object-cover"
            />
          </div>
        ) : (
          <div className="bg-surface h-8 w-8 min-h-8 min-w-8 flex-shrink-0 flex items-center justify-center rounded-md">
            <ImageIcon className="text-muted size-4" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-default truncate">
          {product.title}
        </div>
        <div className="text-2xs text-muted mt-1 line-clamp-2">
          {product.description}
        </div>
      </div>
    </div>
  );
};

const BuildErrorRenderer = ({
  text,
  messageId,
}: {
  text: string;
  messageId?: string;
}) => {
  const { sendChatRequest, files } = useCurrentChatContext();
  const defaultDesignation = useDefaultSelectedElementDesignation();
  const projectId = useCurrentProjectId();
  const chatId = useChatId();
  const { user } = useCurrentUser();
  const [isLogsPopoverOpen, setIsLogsPopoverOpen] = useState(false);

  const { mutate: updateMessage } = chatRouter.updateMessage.useMutation({
    onSuccess: () => {
      void chatUtils.get.invalidate({
        chatId: chatId!,
      });
      void chatUtils.getPaginatedMessages.invalidate({
        chatId: chatId!,
      });
    },
  });

  const validatedText = chatBuildErrorItemSchema.safeParse(JSON.parse(text));
  if (!validatedText.success) {
    console.error("Invalid build error item", validatedText.error);
    return null;
  }

  const buildError = validatedText.data;

  const handleRetry = async () => {
    if (!chatId || !projectId || !messageId) {
      return;
    }

    const prompt = `Please retry the change. ${buildError.logs ? `Here are logs from the previous attempt that may be useful: ${buildError.logs}` : ""}`;
    await sendChatRequest({
      prompt,
      isHidden: true,
      defaultDesignation,
      filesToUpload: files,
    });
    updateMessage({
      id: messageId,
      chatId,
      content: [
        {
          type: "text",
          contentType: "buildError",
          text: JSON.stringify({
            ...buildError,
            hasRetried: true,
          }),
        },
      ],
    });
  };

  return (
    <div className="text-sm w-full">
      <div className="border-0.5 border-border flex flex-col gap-6 rounded-xl p-0 shadow-sm w-full">
        <div className="flex flex-col items-start gap-2 p-4">
          <div className="flex flex-row gap-3 items-start">
            <Button
              icon
              variant="secondary"
              UNSAFE_className="bg-rose-soft border-rose/50"
            >
              <CircleX size={16} className="text-rose" />
            </Button>
            <div className="flex flex-col gap-1">
              <div className="text-sm font-semibold flex items-center gap-2">
                We couldn't make that change
                {user?.isSuperuser && buildError.logs && (
                  <Popover
                    open={isLogsPopoverOpen}
                    onOpenChange={(open) => setIsLogsPopoverOpen(open)}
                    title="Build Error Logs"
                    side="bottom"
                    align="start"
                    triggerAsChild
                    trigger={
                      <Tooltip content="View build error logs">
                        <Button icon variant="tertiary" size="sm">
                          <MoreHorizontal />
                        </Button>
                      </Tooltip>
                    }
                  >
                    <div className="max-w-lg max-h-96 overflow-y-auto">
                      <pre className="text-xs bg-surface p-4 rounded-lg whitespace-pre-wrap font-mono">
                        {buildError.logs ?? "No logs available"}
                      </pre>
                    </div>
                  </Popover>
                )}
              </div>
              <div className="text-xs text-muted relative">
                Something went wrong while applying your edit, so we've safely
                reverted to the previous version.
              </div>
            </div>
          </div>
          <div className="flex w-full justify-end">
            <Button
              loading={buildError.isReverting}
              disabled={buildError.isReverting || buildError.hasRetried}
              variant="secondary"
              size="sm"
              onClick={() => {
                void handleRetry();
              }}
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const GeneralErrorRenderer = ({
  type,
  errorId,
  message,
}: {
  type: "networkError" | "noNetworkError" | "unknownError";
  errorId?: string;
  message?: string;
}) => {
  const isDebugMode = useIsDebugMode();

  return (
    <div className="text-sm w-full">
      <div className="border-0.5 border-border flex flex-col gap-6 rounded-xl p-0 shadow-sm w-full">
        <div className="flex flex-col items-start gap-2 p-4">
          <div className="flex flex-row gap-3 items-start">
            <Button
              icon
              variant="secondary"
              UNSAFE_className="bg-rose-soft border-rose/50"
            >
              <CircleX size={16} className="text-rose" />
            </Button>
            <div className="flex flex-col gap-1">
              <div className="text-sm font-semibold flex items-center gap-2">
                {exhaustiveSwitch({ type })({
                  networkError: () => "Network Connection Lost",
                  noNetworkError: () => "No Network Connection",
                  unknownError: () => "An Unknown Error Occurred",
                })}
              </div>
              <div className="text-xs text-muted relative">
                {exhaustiveSwitch({ type })({
                  networkError: () => {
                    return "Looks like your network lost connection to the server. Please try again.";
                  },
                  noNetworkError: () => {
                    return "Looks like you're offline. Please check your connection and try again.";
                  },
                  unknownError: () => {
                    return isDebugMode && message
                      ? message
                      : "There was an error processing your chat. Please try again, or reach out to support@replo.app if this error persists.";
                  },
                })}
              </div>
              {isDebugMode && errorId && (
                <div className="text-2xs text-muted mt-1 font-mono">
                  Error ID: {errorId}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const RevertRenderer = ({ text }: { text: string }) => {
  const parsedText = parseJsonOrNull(text);

  const validatedText = chatRevertItemSchema.safeParse(parsedText);
  const { fileVersion, isFileVersionLoading } = useViewingVersion(
    validatedText?.data?.fileVersionId,
  );

  if (!validatedText.success) {
    console.error("Invalid revert item", validatedText.error);
    return null;
  }

  if (isFileVersionLoading) {
    return <VersionCardSkeleton />;
  }

  if (!fileVersion) {
    return null;
  }

  return (
    <VersionCard
      version={fileVersion}
      currentPagePreviewVersionClassName="mr-1"
    />
  );
};

export const TextMessage = ({ text, role }: { text: string; role: string }) => {
  return (
    <div
      className={twMerge(
        "text-sm relative [word-break:break-word] text-default max-w-full",
        role === "user"
          ? "px-3 py-2 bg-surface rounded-2xl rounded-tr-sm ml-auto"
          : "rounded-lg",
      )}
    >
      <MarkdownRenderer>{text}</MarkdownRenderer>
    </div>
  );
};

interface TextMessageItemProps {
  item: TextMessageContent;
  role: string;
  messageId?: string;
}

export function TextMessageItem({
  item,
  role,
  messageId,
}: TextMessageItemProps) {
  const isAssistant = role === "assistant";
  const {
    thinkBlocks,
    cleanedText,
    hasThinkBlocks,
    isStreamingThink,
    streamingThinkContent,
  } = extractThinkBlocks(item.text);

  const isThinkingDefaultOpen = isStreamingThink;
  const [isThinkingOpen, setIsThinkingOpen] = useState(isThinkingDefaultOpen);
  useOnValueChange(isStreamingThink, (currentValue, previousValue) => {
    if (previousValue && !currentValue) {
      setIsThinkingOpen(false);
    }
  });

  if (item.isHidden) {
    return null;
  }

  // Default text content (contentType === "text" or no contentType)
  if (item.contentType === "text" || !item.contentType) {
    const shouldShowThinkingBlock =
      isAssistant && (hasThinkBlocks || isStreamingThink);

    return (
      <div className="flex flex-col w-full">
        {shouldShowThinkingBlock && (
          <div className="w-full mb-1">
            <Collapsible
              key={isStreamingThink ? "streaming" : "completed"}
              defaultOpen={isThinkingDefaultOpen}
              open={isThinkingOpen}
              onOpenChange={setIsThinkingOpen}
              trigger={
                <button
                  type="button"
                  className="h-6 flex items-center gap-1 text-sm text-muted hover:text-default transition-colors cursor-pointer ml-[-4px]"
                >
                  <Lightbulb size={16} />{" "}
                  {isThinkingOpen ? "Hide thinking" : "Show thinking"}
                </button>
              }
            >
              <div className="border-l border-border pl-2 py-1 ml-[3.5px]">
                <div className="text-sm text-muted/80 leading-relaxed italic">
                  {isStreamingThink ? (
                    <MarkdownRenderer>{streamingThinkContent}</MarkdownRenderer>
                  ) : (
                    thinkBlocks.map((block, index) => (
                      <div key={index} className="mb-2 last:mb-0">
                        <MarkdownRenderer>{block}</MarkdownRenderer>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </Collapsible>
          </div>
        )}

        <TextMessage
          text={shouldShowThinkingBlock ? cleanedText : item.text}
          role={role}
        />
      </div>
    );
  }

  return exhaustiveSwitch(
    item as TextMessageContent & {
      contentType: NonNullable<TextMessageContent["contentType"]>;
    },
    "contentType",
  )({
    product: (item) => <ProductRenderer text={item.text} />,
    buildError: (item) => (
      <BuildErrorRenderer text={item.text} messageId={messageId} />
    ),
    revert: (item) => <RevertRenderer text={item.text} />,
    codeChangesOverview: () => null,
    generalError: (item) => {
      const generalError = chatGeneralErrorItemSchema.safeParse(
        JSON.parse(item.text),
      );

      if (!generalError.success) {
        console.error("Invalid general error item", generalError.error);
        return null;
      }

      return (
        <GeneralErrorRenderer
          type={generalError.data.type}
          errorId={generalError.data.errorId}
          message={generalError.data.message}
        />
      );
    },
    canceled: () => <CanceledCard />,
    text: () => null,
    selectedTemplate: () => null,
    generatedPage: () => null,
    toolSectionHeader: () => null,
    // NOTE (Ryan, 2026-02-03): Integration status cards are only rendered in agent-ui
    integrationStatus: () => null,
  });
}
